import React, { useState, useEffect, useRef } from 'react';
import './ChatInterface.css';
import { AI_PERSONAS, USER_PERSONA } from '../data/aiPersonas';
import { getTopicById } from '../data/debateTopics';
import MessageBubble from './MessageBubble';
import ParticipantList from './ParticipantList';
import TopicSelector from './TopicSelector';
import ConversationControls from './ConversationControls';
import UserInput from './UserInput';
import ExportModal from './ExportModal';
import { NotificationContainer, useNotifications } from './Notification';
import { generateMessageId, validateMessage, formatTime } from '../utils/helpers';
import TopicSelectionModal from './TopicSelectionModal';
import { generateAIResponse, selectRespondingDoctor, isOpenAIAvailable } from '../services/openaiService';
import SuggestedQuestions from './SuggestedQuestions';

const ChatInterface = () => {
  const [currentTopic, setCurrentTopic] = useState('anxiety_treatment');
  const [messages, setMessages] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [userMessages, setUserMessages] = useState([]);
  const [typingIndicator, setTypingIndicator] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [showTopicSelection, setShowTopicSelection] = useState(true);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [showSuggestedQuestions, setShowSuggestedQuestions] = useState(false);
  const { notifications, addNotification, removeNotification } = useNotifications();
  
  const intervalRef = useRef(null);
  const messageIndexRef = useRef(0);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, userMessages, typingIndicator]);

  useEffect(() => {
    if (isOpenAIAvailable()) {
      addNotification('OpenAI integration enabled - AI therapists will respond to your messages!', 'success');
    }
  }, [addNotification]);

  const startConversation = (selectedTopicId = null) => {
    const topicId = selectedTopicId || currentTopic;
    setCurrentTopic(topicId);
    setShowTopicSelection(false);
    setConversationStarted(true);
    setIsPlaying(false); // Don't auto-start the scripted conversation
    setMessages([]);
    setUserMessages([]);
    setCurrentTime(0);
    messageIndexRef.current = 0;
    
    const topic = getTopicById(topicId);
    if (!topic) {
      addNotification('Topic not found. Please try again.', 'error');
      return;
    }

    addNotification(`Ready to discuss: ${topic.title}. Ask your first question!`, 'success');
    
    // Add a welcome message to guide the user
    const welcomeMessage = {
      id: generateMessageId(),
      speaker: 'dr_sarah',
      timestamp: Date.now() / 1000,
      content: `Welcome to our discussion on "${topic.title}"! I'm Dr. Sarah Chen, and I'm here with Dr. James Williams and Dr. Maria Rodriguez. We'd love to hear your thoughts and questions about ${topic.description.toLowerCase()}. What would you like to explore?`,
      isUser: false,
      isWelcomeMessage: true
    };
    
    setMessages([welcomeMessage]);
    setShowSuggestedQuestions(true);
  };

  const startScriptedDebate = () => {
    setIsPlaying(true);
    
    const topic = getTopicById(currentTopic);
    if (!topic) {
      addNotification('Topic not found. Please try again.', 'error');
      return;
    }

    addNotification(`Starting AI debate on: ${topic.title}`, 'success');

    intervalRef.current = setInterval(() => {
      setCurrentTime(prevTime => {
        const newTime = prevTime + (100 / playbackSpeed);
        
        // Check if we should display the next message
        const topic = getTopicById(currentTopic);
        const nextMessage = topic.messages[messageIndexRef.current];
        
        if (nextMessage && newTime >= nextMessage.timestamp * 100) {
          // Show typing indicator
          setTypingIndicator(nextMessage.speaker);
          
          // After typing delay, show the message
          setTimeout(() => {
            setMessages(prev => [...prev, {
              ...nextMessage,
              id: generateMessageId()
            }]);
            setTypingIndicator(null);
            messageIndexRef.current++;
          }, nextMessage.typingDelay);
        }
        
        return newTime;
      });
    }, 100);
  };

  const pauseConversation = () => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const resumeConversation = () => {
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setCurrentTime(prevTime => {
        const newTime = prevTime + (100 / playbackSpeed);
        
        const topic = getTopicById(currentTopic);
        const nextMessage = topic.messages[messageIndexRef.current];
        
        if (nextMessage && newTime >= nextMessage.timestamp * 100) {
          setTypingIndicator(nextMessage.speaker);
          
          setTimeout(() => {
            setMessages(prev => [...prev, {
              ...nextMessage,
              id: `msg_${nextMessage.id}_${Date.now()}`
            }]);
            setTypingIndicator(null);
            messageIndexRef.current++;
          }, nextMessage.typingDelay);
        }
        
        return newTime;
      });
    }, 100);
  };

  const resetConversation = () => {
    pauseConversation();
    setMessages([]);
    setUserMessages([]);
    setCurrentTime(0);
    messageIndexRef.current = 0;
    setConversationStarted(false);
    setTypingIndicator(null);
    setShowTopicSelection(true);
    setShowSuggestedQuestions(false);
  };

  const changeTopic = (newTopicId) => {
    if (newTopicId !== currentTopic) {
      setCurrentTopic(newTopicId);
      resetConversation();
    }
  };

  const handleUserMessage = async (messageContent) => {
    const validation = validateMessage(messageContent);
    
    if (!validation.isValid) {
      if (validation.isEmpty) {
        addNotification('Please enter a message before sending.', 'warning');
      } else if (validation.isTooLong) {
        addNotification(`Message too long. Maximum ${validation.maxLength} characters allowed.`, 'error');
      }
      return;
    }
    
    const currentTimestamp = Date.now() / 1000; // Use actual timestamp
    const userMessage = {
      id: generateMessageId(),
      speaker: 'user',
      timestamp: currentTimestamp,
      content: messageContent.trim(),
      isUser: true
    };
    
    setUserMessages(prev => [...prev, userMessage]);
    setShowSuggestedQuestions(false); // Hide suggested questions after user sends a message
    addNotification('Message sent successfully!', 'success');
    
    // Generate AI response if OpenAI is available
    if (isOpenAIAvailable()) {
      await generateAndAddAIResponse(userMessage);
    }
  };

  const generateAndAddAIResponse = async (userMessage) => {
    setIsGeneratingResponse(true);
    
    try {
      const allMessages = [...messages, ...userMessages];
      const respondingDoctor = selectRespondingDoctor(userMessage.content, allMessages);
      
      // Show typing indicator
      setTypingIndicator(respondingDoctor);
      
      const aiResponse = await generateAIResponse(
        userMessage.content,
        currentTopic,
        allMessages,
        respondingDoctor
      );
      
      // Remove typing indicator
      setTypingIndicator(null);
      
      // Add AI response
      const aiMessage = {
        id: generateMessageId(),
        speaker: respondingDoctor,
        timestamp: Date.now() / 1000, // Use actual timestamp
        content: aiResponse,
        isUser: false,
        isAIResponse: true
      };
      
      setMessages(prev => [...prev, aiMessage]);
      addNotification(`${getSpeakerInfo(respondingDoctor).name} responded!`, 'success');
      
    } catch (error) {
      console.error('Error generating AI response:', error);
      setTypingIndicator(null);
      addNotification('AI response failed. Please try again.', 'error');
    } finally {
      setIsGeneratingResponse(false);
    }
  };

  const getCurrentTimeFormatted = () => {
    return formatTime(currentTime / 100);
  };

  const getSpeakerInfo = (speakerId) => {
    if (speakerId === 'user') return USER_PERSONA;
    return AI_PERSONAS[speakerId.toUpperCase()];
  };

  const allMessages = [...messages, ...userMessages].sort((a, b) => a.timestamp - b.timestamp);

  const topic = getTopicById(currentTopic);

  if (showTopicSelection) {
    return (
      <div className="chat-interface">
        <NotificationContainer 
          notifications={notifications}
          onRemoveNotification={removeNotification}
        />
        <TopicSelectionModal 
          onTopicSelect={startConversation}
          currentTopic={currentTopic}
          onTopicChange={setCurrentTopic}
        />
      </div>
    );
  }

  return (
    <div className="chat-interface">
      <NotificationContainer 
        notifications={notifications}
        onRemoveNotification={removeNotification}
      />
      
      <div className="chat-header">
        <div className="header-title">
          <h1>Multi-AI Chat Demo</h1>
          <p>Three AI therapists debate mental health topics with user participation</p>
        </div>
        <div className="header-controls">
          <TopicSelector 
            currentTopic={currentTopic}
            onTopicChange={changeTopic}
          />
          <ConversationControls
            isPlaying={isPlaying}
            onPlay={startScriptedDebate}
            onPause={pauseConversation}
            onResume={resumeConversation}
            onReset={resetConversation}
            playbackSpeed={playbackSpeed}
            onSpeedChange={setPlaybackSpeed}
            onExport={() => setShowExportModal(true)}
            currentTime={getCurrentTimeFormatted()}
            conversationStarted={conversationStarted}
          />
        </div>
      </div>

      <div className="chat-container">
        <ParticipantList 
          participants={Object.values(AI_PERSONAS)}
          user={USER_PERSONA}
          currentSpeaker={typingIndicator}
        />
        
        <div className="conversation-area">
          <div className="conversation-header">
            <h2>Current Topic: {topic?.title}</h2>
            <p>{topic?.description}</p>
          </div>
          
          <div className="messages-container">
            {allMessages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                speaker={getSpeakerInfo(message.speaker)}
                timestamp={message.timestamp}
              />
            ))}
            
            {typingIndicator && (
              <div className="typing-indicator">
                <div className="typing-avatar">
                  {getSpeakerInfo(typingIndicator).avatar}
                </div>
                <div className="typing-content">
                  <div className="typing-name">
                    {getSpeakerInfo(typingIndicator).name} is typing...
                  </div>
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            {showSuggestedQuestions && !isPlaying && (
              <SuggestedQuestions 
                topicId={currentTopic}
                onQuestionSelect={(question) => {
                  handleUserMessage(question);
                }}
              />
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          <UserInput 
            onSendMessage={handleUserMessage}
            disabled={!conversationStarted || isGeneratingResponse}
            isGeneratingResponse={isGeneratingResponse}
          />
        </div>
      </div>

      {showExportModal && (
        <ExportModal
          messages={allMessages}
          topic={topic}
          onClose={() => setShowExportModal(false)}
          getSpeakerInfo={getSpeakerInfo}
        />
      )}
    </div>
  );
};

export default ChatInterface;
